import { useState } from 'react';
import { VStack, Text, Heading } from 'native-base';

// Components
import {
  PrimaryButton,
  ModalWrapper,
  TextField,
  LocalComponentError,
} from '../../';

// Redux
import { useDispatch } from 'react-redux';
import { openModal } from '@breakroom-nx/redux';

import * as Yup from 'yup';

import { Formik, ErrorMessage } from 'formik';

// Assets
import { theme } from '@breakroom-nx/assets';

import { InputType, Status } from '@breakroom-nx/helpers';
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  deleteUser,
} from 'firebase/auth';

const AccountDeletionSchema = Yup.object().shape({
  password: Yup.string().required('Please enter your password'),
});

export const AccountDeletion = () => {
  const initialValues = { password: '' };
  const auth = getAuth();
  const user = auth.currentUser;

  const dispatch = useDispatch();
  const [deleting, setDeleting] = useState<boolean>(false);
  const [textError, setTextError] = useState<string | undefined>(undefined);

  const deleteAccount = async (password?: string) => {
    setDeleting(true);
    const user = auth.currentUser;

    try {
      if (!user) throw new Error('Auth credentials do not exist');

      if (!user.email) {
        // Deleting Anonymous Account
        await deleteUser(auth.currentUser)
          .then((deleted) => {
            setDeleting(false);
            localStorage.clear();
            dispatch(
              openModal({
                modalType: 'Alert',
                modalProps: {
                  status: Status.success,
                  title:
                    'Your account has been deleted. We hope to see you again!',
                },
              })
            );
          })
          .catch((error) => {
            throw new Error('Error deleting account, please try again.');
          });
      } else {
        // Deleting Registered Account
        if (!password) throw new Error('Please provide your current password');

        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential)
          .then(async () => {
            await deleteUser(user)
              .then(() => {
                setDeleting(false);
                localStorage.clear();
                dispatch(
                  openModal({
                    modalType: 'Alert',
                    modalProps: {
                      status: Status.success,
                      title:
                        'Your account has been deleted. We hope to see you again!',
                    },
                  })
                );
              })
              .catch((error) => {
                throw new Error('Error deleting account. Please try again.');
              });
          })
          .catch((error) => {
            throw new Error('Please check your password and try again.');
          });
      }
    } catch (err: unknown) {
      const error = err as Error;
      setDeleting(false);
      setTextError(error.message);
    }
  };

  return (
    <ModalWrapper>
      <VStack
        bg={'surface.100'}
        space={3}
        textAlign={'center'}
        style={{
          borderColor: theme.colors.surface[200],
          borderWidth: 1,
          borderRadius: 25,
          shadowColor: theme.colors.surface[100],
          shadowOpacity: 0.25,
          shadowRadius: 20,
          shadowOffset: {
            width: 10,
            height: 20,
          },
          padding: 30,
        }}
      >
        <VStack space={'5'}>
          <VStack space={4}>
            <LocalComponentError error={textError} />
            <Heading fontSize={30} lineHeight={34}>
              <Text color={'red.400'}>Account Deletion</Text>
            </Heading>
            <Text
              fontFamily={'heading'}
              fontSize={16}
              color={'red.300'}
              fontWeight={600}
            >
              Account deletion is permanent. We delete your entire account,
              release your username for others to use and wipe all data
              associated with you.
            </Text>

            {user && user.email ? (
              <>
                <Text
                  fontFamily={'heading'}
                  fontSize={16}
                  color={'red.300'}
                  fontWeight={600}
                >
                  Please enter your password to delete your account.
                </Text>

                <Formik
                  initialValues={initialValues}
                  validationSchema={AccountDeletionSchema}
                  onSubmit={async (values) => {
                    await deleteAccount(values.password);
                  }}
                >
                  {({ values, handleChange, handleBlur, handleSubmit }) => (
                    <VStack width={'full'} space={5}>
                      <TextField
                        disabled={deleting}
                        type={InputType.Password}
                        value={values.password}
                        onChangeText={handleChange('password')}
                        onBlur={handleBlur('password')}
                        onSubmitEditing={handleSubmit}
                        autoCompleteType={'password'}
                      />
                      <Text variant={'error'}>
                        <ErrorMessage name="password" />
                      </Text>
                      <PrimaryButton
                        disabled={deleting}
                        isLoading={deleting}
                        text="Confirm Deletion"
                        bg={'red.400'}
                        onPress={handleSubmit}
                      />
                    </VStack>
                  )}
                </Formik>
              </>
            ) : (
              <PrimaryButton
                disabled={deleting}
                isLoading={deleting}
                text="Confirm Deletion"
                bg={'red.400'}
                onPress={deleteAccount}
              />
            )}
          </VStack>
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
